// use-opportunities.ts — Full opportunity list for Opportunity Radar.
// Radar must evaluate ALL opportunities, not just the mission top-5.
//
// PHASE 1 AUTH GATE: Gated on useAuthEnabled() so this query never fires before
// the Supabase session has resolved and a Bearer token is available. Without this
// gate, the query fired on mount (before auth) → COOKIE_MISSING 401 → stuck in
// error state for the page lifetime. With the gate, the query stays in "pending"
// until auth resolves, then fires with a valid Bearer token already injected by
// bearerFetchPatch.ts into the underlying window.fetch call.

import { useQuery } from "@tanstack/react-query";
import { useAuthEnabled } from "./useAuthEnabled";
import { authFetch } from "./authFetch";

export interface RawOpportunity {
  id: number;
  title: string;
  companyId: number | null;
  companyName: string | null;
  value: number | null;
  status: string;
  notes: string | null;
  createdAt: string; // ISO
}

async function fetchOpportunities(): Promise<RawOpportunity[]> {
  const res = await authFetch("/api/opportunities");
  if (!res.ok) throw new Error("Failed to fetch opportunities");
  return res.json() as Promise<RawOpportunity[]>;
}

export const OPPORTUNITIES_QUERY_KEY = ["opportunities"] as const;

export function useGetOpportunities() {
  const authEnabled = useAuthEnabled();
  return useQuery<RawOpportunity[]>({
    queryKey: OPPORTUNITIES_QUERY_KEY,
    queryFn: fetchOpportunities,
    enabled: authEnabled,
  });
}
